import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import logo from '../../asset/logo/large-white.svg';
import { authAxios, useAuth } from '../../auth';
import './Reader.css';
import { AudioPlayButton } from '../shared/AudioPlayButton';
import { API } from '../../config';

import {
    Layout,
    Menu,
    Dropdown,
    Table,
    Modal,
    Tooltip,
    Button,
    Space
} from 'antd';
import { DownOutlined, EditOutlined, PlayCircleOutlined, PauseCircleOutlined, StopOutlined, CaretRightOutlined, UserOutlined, LogoutOutlined } from '@ant-design/icons';

const { Header, Content, Footer } = Layout;

const nt_books = [
    'matthew', 'mark', 'luke', 'john', 'acts',
    'romans', '1corinthians', '2corinthians', 'galatians', 'ephesians',
    'philippians', 'colossians', '1thessalonians', '2thessalonians', '1timothy',
    '2timothy', 'titus', 'philemon', 'hebrews', 'james',
    '1peter', '2peter', '1john', '2john', '3john',
    'jude', 'revelation'
];
const ot_books = ['genesis'];

// Helper to determine language from book name
const getLangFromBook = (book: string): string => {
    return ot_books.includes(book) ? 'hebrew' : 'greek';
};

const ToTitleCase = ((str: string | undefined) =>
    str === undefined ? '' : str.toLowerCase().split(' ').map((word: string) =>
        word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' '));

// Cache for glosses to avoid repeated API calls
const glossCache: { [key: string]: string } = {};

// Check if definition_in_context is valid (exists and not all asterisks)
const isValidDefinition = (def: string | null | undefined): boolean => {
    if (!def || def.trim() === '') return false;
    if (/^\*+$/.test(def.trim())) return false;
    return true;
};

// WordLink component with hover tooltip
const WordLink: React.FC<{
    verseData: any;
    lang: string;
    onWordClick: (verseData: any, glossaryData: any) => void;
    onPlayFromHere?: () => void;
    isHighlighted?: boolean;
    style?: React.CSSProperties;
}> = ({ verseData, lang, onWordClick, onPlayFromHere, isHighlighted = false, style }) => {
    const hasValidDefinition = isValidDefinition(verseData.definition_in_context);
    const [tooltipText, setTooltipText] = React.useState<string>(
        hasValidDefinition ? verseData.definition_in_context : 'Loading...'
    );
    const [hasFetched, setHasFetched] = React.useState<boolean>(hasValidDefinition);

    // Reset state when verseData changes
    React.useEffect(() => {
        const valid = isValidDefinition(verseData.definition_in_context);
        setTooltipText(valid ? verseData.definition_in_context : 'Loading...');
        setHasFetched(valid);
    }, [verseData.id, verseData.word, verseData.definition_in_context]);

    const handleMouseEnter = () => {
        if (hasFetched) return;

        const cacheKey = `${lang}:${verseData.word}`;

        if (glossCache[cacheKey] !== undefined) {
            setTooltipText(glossCache[cacheKey] || 'No definition available');
            setHasFetched(true);
            return;
        }

        const glossaryUrl = lang === 'hebrew' ? API.hebrewGlossary(verseData.word) : API.greekGlossary(verseData.word);
        authAxios.get(glossaryUrl)
            .then((res: { data: any }) => {
                const gloss = res.data?.gloss || '';
                glossCache[cacheKey] = gloss;
                setTooltipText(gloss || 'No definition available');
            })
            .catch(() => {
                glossCache[cacheKey] = '';
                setTooltipText('No definition available');
            })
            .finally(() => {
                setHasFetched(true);
            });
    };

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        const glossaryUrl = lang === 'hebrew' ? API.hebrewGlossary(verseData.word) : API.greekGlossary(verseData.word);
        authAxios.get(glossaryUrl)
            .then((res: { data: any }) => {
                onWordClick(verseData, res.data);
            })
            .catch(err => console.log(err));
    };

    const contextMenu = (
        <Menu>
            <Menu.Item key="play" icon={<CaretRightOutlined />} onClick={onPlayFromHere}>
                Play from here
            </Menu.Item>
        </Menu>
    );

    return (
        <Dropdown overlay={contextMenu} trigger={['contextMenu']}>
            <Tooltip title={tooltipText}>
                <span
                    className={`word-link ${isHighlighted ? 'word-link-active' : ''}`}
                    style={style}
                    onMouseEnter={handleMouseEnter}
                    onClick={handleClick}
                >
                    {verseData.word}
                </span>
            </Tooltip>
        </Dropdown>
    );
};

const columns = (lang: string) => [
    {
        title: 'Word',
        key: 'word',
        dataIndex: 'word',
        render: (text: string) => (
            <span className={lang} style={{ fontSize: 18 }}>{text}</span>
        ),
    },
    { title: 'Definition in Context', key: 'definition_in_context', dataIndex: 'definition_in_context' },
    {
        title: 'Lexical Form',
        key: 'lexical_form',
        dataIndex: 'lexical_form',
        render: (text: string) => (
            <span className={lang} style={{ fontSize: 18 }}>{text}</span>
        ),
    },
    { title: 'Gloss', key: 'gloss', dataIndex: 'gloss' },
    { title: 'Part of Speech', key: 'part_of_speech', dataIndex: 'part_of_speech' },
    { title: 'Declension', key: 'declension', dataIndex: 'declension' },
    { title: 'Case', key: 'case', dataIndex: 'case' },
    { title: 'Number', key: 'number', dataIndex: 'number' },
    { title: 'Gender', key: 'gender', dataIndex: 'gender' },
    { title: 'Mood', key: 'mood', dataIndex: 'mood' },
    { title: 'Voice', key: 'voice', dataIndex: 'voice' },
    { title: 'Tense', key: 'tense', dataIndex: 'tense' },
    { title: 'Person', key: 'person', dataIndex: 'person' },
    { title: 'Stem', key: 'stem', dataIndex: 'stem' },
    { title: 'Logged', key: 'occurences', dataIndex: 'occurences', align: 'right' as const },
];

const filterColumns = (definition: any, lang: string, word: string | null) => {
    const allColumns: any[] = columns(lang);
    // Replace the Word column with one that includes the AudioPlayButton
    const wordColumnIndex = allColumns.findIndex(c => c.key === 'word');
    if (wordColumnIndex >= 0 && word) {
        allColumns[wordColumnIndex] = {
            ...allColumns[wordColumnIndex],
            render: (text: string) => (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className={lang} style={{ fontSize: 18 }}>{text}</span>
                    <AudioPlayButton language={lang} word={word} size="small" />
                </span>
            ),
        };
    }

    let showColumns: any[] = [];
    allColumns.forEach((c: any) => {
        let check = definition[c.key];
        if (check != null && check.toString().length > 0) {
            showColumns.push(c);
        }
    });
    return showColumns;
};

function Reader() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const initialBook = searchParams.get('book') || '3john';
    const initialChapter = parseInt(searchParams.get('chapter') || '1', 10);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const [loading, setLoading] = useState<boolean>(true);
    const [lang, setLang] = useState<string>(getLangFromBook(initialBook));
    const [allVerses, setAllVerses] = useState<any[]>([]);
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const [book, setBook] = useState(initialBook);
    const [totalChapters, setTotalChapters] = useState<any[]>([]);
    const [chapter, setChapter] = useState(initialChapter);
    const [definition, setDefinition] = useState<any>({});
    const [selectedOccurrence, setSelectedOccurrence] = useState<any>(null);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

    // Audio playback state
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [isPaused, setIsPaused] = useState<boolean>(false);
    const [currentWordIndex, setCurrentWordIndex] = useState<number>(-1);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const playbackActiveRef = useRef<boolean>(false);

    // Group verses by verse number
    const verseGroups = React.useMemo(() => {
        const groups: { [key: number]: any[] } = {};
        allVerses.forEach(v => {
            if (!groups[v.verse]) {
                groups[v.verse] = [];
            }
            groups[v.verse].push(v);
        });
        // Sort words within each verse by position
        Object.keys(groups).forEach(key => {
            groups[parseInt(key)].sort((a, b) => a.position - b.position);
        });
        return groups;
    }, [allVerses]);

    const verseNumbers = Object.keys(verseGroups).map(Number).sort((a, b) => a - b);

    // Update URL when book or chapter changes
    useEffect(() => {
        setSearchParams({ book, chapter: chapter.toString() });
    }, [book, chapter, setSearchParams]);

    useEffect(() => {
        authAxios.get(API.chapters(book))
            .then((res: { data: any; }) => {
                setTotalChapters(res.data);
            }).catch(e => console.log(e));
        authAxios.get(API.bible(book, chapter))
            .then((res: { data: any; }) => {
                let v = res.data;
                v.sort((a: any, b: any) =>
                    a.chapter - b.chapter || a.verse - b.verse || a.position - b.position
                );
                setAllVerses(v);
                setLoading(false);
            }).catch(e => console.log(e));
    }, [refreshKey, book, chapter]);

    const handleWordClick = (verseData: any, glossaryData: any) => {
        setSelectedOccurrence(verseData);
        setDefinition(glossaryData);
        setIsModalVisible(true);
    };

    const closeModal = () => {
        setIsModalVisible(false);
        setDefinition({});
        setSelectedOccurrence(null);
    };

    // Check if audio exists for a word
    const checkAudioExists = useCallback(async (word: string): Promise<boolean> => {
        try {
            await authAxios.head(API.audioCheck(lang, word));
            return true;
        } catch {
            return false;
        }
    }, [lang]);

    // Play audio for a specific word (fetch with auth headers)
    const playWordAudio = useCallback(async (word: string): Promise<void> => {
        try {
            // Fetch audio with auth header and create blob URL
            const response = await authAxios.get(API.audioGet(lang, word), {
                responseType: 'blob'
            });
            const blobUrl = URL.createObjectURL(response.data);

            return new Promise((resolve) => {
                const audio = new Audio(blobUrl);
                audioRef.current = audio;

                audio.onended = () => {
                    audioRef.current = null;
                    URL.revokeObjectURL(blobUrl);
                    resolve();
                };

                audio.onerror = () => {
                    audioRef.current = null;
                    URL.revokeObjectURL(blobUrl);
                    resolve();
                };

                audio.play().catch(() => {
                    audioRef.current = null;
                    URL.revokeObjectURL(blobUrl);
                    resolve();
                });
            });
        } catch {
            // Audio fetch failed, just resolve
            return Promise.resolve();
        }
    }, [lang]);

    // Sequential playback logic
    const playNextWord = useCallback(async (startIndex: number) => {
        if (!playbackActiveRef.current || startIndex >= allVerses.length) {
            // Playback finished or stopped
            setIsPlaying(false);
            setIsPaused(false);
            setCurrentWordIndex(-1);
            playbackActiveRef.current = false;
            return;
        }

        const word = allVerses[startIndex];
        setCurrentWordIndex(startIndex);

        // Check if audio exists for this word
        const hasAudio = await checkAudioExists(word.word);

        if (hasAudio && playbackActiveRef.current) {
            // Play audio and wait for it to finish
            await playWordAudio(word.word);
        } else if (playbackActiveRef.current) {
            // No audio - pause briefly to show the word
            await new Promise(resolve => setTimeout(resolve, 400));
        }

        // Move to next word if still playing
        if (playbackActiveRef.current) {
            playNextWord(startIndex + 1);
        }
    }, [allVerses, checkAudioExists, playWordAudio]);

    // Start playback
    const startPlayback = useCallback(() => {
        if (allVerses.length === 0) return;

        playbackActiveRef.current = true;
        setIsPlaying(true);
        setIsPaused(false);

        // Start from current position or beginning
        const startIndex = currentWordIndex >= 0 ? currentWordIndex : 0;
        playNextWord(startIndex);
    }, [allVerses.length, currentWordIndex, playNextWord]);

    // Start playback from a specific index
    const startPlaybackFromIndex = useCallback((index: number) => {
        if (allVerses.length === 0 || index < 0 || index >= allVerses.length) return;

        playbackActiveRef.current = true;
        setIsPlaying(true);
        setIsPaused(false);
        playNextWord(index);
    }, [allVerses.length, playNextWord]);

    // Pause playback
    const pausePlayback = useCallback(() => {
        playbackActiveRef.current = false;
        setIsPaused(true);
        setIsPlaying(false);

        // Stop current audio
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
    }, []);

    // Resume playback
    const resumePlayback = useCallback(() => {
        if (currentWordIndex < 0) return;

        playbackActiveRef.current = true;
        setIsPlaying(true);
        setIsPaused(false);
        playNextWord(currentWordIndex);
    }, [currentWordIndex, playNextWord]);

    // Stop playback
    const stopPlayback = useCallback(() => {
        playbackActiveRef.current = false;
        setIsPlaying(false);
        setIsPaused(false);
        setCurrentWordIndex(-1);

        // Stop current audio
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
    }, []);

    // Stop playback when chapter/book changes
    useEffect(() => {
        stopPlayback();
    }, [book, chapter, stopPlayback]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            playbackActiveRef.current = false;
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    // Get current word ID for highlighting
    const currentWordId = currentWordIndex >= 0 && currentWordIndex < allVerses.length
        ? allVerses[currentWordIndex].id
        : null;

    return (
        <div className="Reader">
            <Layout className="layout" style={{ minHeight: '100vh' }}>
                <Header className="Reader-Header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <img src={logo} className="Reader-logo" alt="logo" />
                    <Space>
                        {/* Audio Playback Controls */}
                        {!isPlaying && !isPaused && (
                            <Button
                                type="primary"
                                icon={<PlayCircleOutlined />}
                                onClick={startPlayback}
                                disabled={loading || allVerses.length === 0}
                            >
                                Play Chapter
                            </Button>
                        )}
                        {isPlaying && (
                            <Button
                                icon={<PauseCircleOutlined />}
                                onClick={pausePlayback}
                            >
                                Pause
                            </Button>
                        )}
                        {isPaused && (
                            <>
                                <Button
                                    type="primary"
                                    icon={<PlayCircleOutlined />}
                                    onClick={resumePlayback}
                                >
                                    Resume
                                </Button>
                                <Button
                                    icon={<StopOutlined />}
                                    onClick={stopPlayback}
                                >
                                    Stop
                                </Button>
                            </>
                        )}
                        {isPlaying && (
                            <Button
                                danger
                                icon={<StopOutlined />}
                                onClick={stopPlayback}
                            >
                                Stop
                            </Button>
                        )}
                        <Link to={`/populate?book=${book}&chapter=${chapter}&verse=1`}>
                            <Button type="default" icon={<EditOutlined />}>
                                Edit Mode
                            </Button>
                        </Link>
                        <Dropdown
                            trigger={['click']}
                            overlay={
                                <Menu>
                                    <Menu.Item key="email" disabled style={{ cursor: 'default' }}>
                                        {user?.email}
                                    </Menu.Item>
                                    <Menu.Divider />
                                    <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={handleLogout}>
                                        Logout
                                    </Menu.Item>
                                </Menu>
                            }
                        >
                            <Button icon={<UserOutlined />} />
                        </Dropdown>
                    </Space>
                </Header>
                {loading ? (
                    <Content style={{ padding: '30px 50px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
                        <div style={{ color: '#888', fontSize: 14 }}>Loading...</div>
                    </Content>
                ) : (
                    <Content key={`${book}-${chapter}-${lang}`} className="content-fade-in" style={{ padding: '30px 50px' }}>
                        <div style={{ marginBottom: 24 }}>
                            <Dropdown
                                trigger={['click']}
                                overlay={
                                    <Menu>
                                        {ot_books.map(b =>
                                            <Menu.Item key={b}>
                                                <a
                                                    onClick={e => {
                                                        e.preventDefault();
                                                        setLoading(true);
                                                        setBook(b);
                                                        setChapter(1);
                                                        setLang("hebrew");
                                                        setRefreshKey(refreshKey + 1);
                                                    }}
                                                >
                                                    {ToTitleCase(b.match(/[^\d]+|\d+/g)?.join(' '))}
                                                </a>
                                            </Menu.Item>
                                        )}
                                        {nt_books.map(b =>
                                            <Menu.Item key={b}>
                                                <a
                                                    onClick={e => {
                                                        e.preventDefault();
                                                        setLoading(true);
                                                        setBook(b);
                                                        setChapter(1);
                                                        setLang("greek");
                                                        setRefreshKey(refreshKey + 1);
                                                    }}
                                                >
                                                    {ToTitleCase(b.match(/[^\d]+|\d+/g)?.join(' '))}
                                                </a>
                                            </Menu.Item>
                                        )}
                                    </Menu>
                                }
                            >
                                <span style={{ cursor: "pointer", marginRight: 20, fontSize: 24, fontWeight: 600 }}>
                                    {ToTitleCase(book.match(/[^\d]+|\d+/g)?.join(' '))} <DownOutlined />
                                </span>
                            </Dropdown>
                            <Dropdown
                                trigger={['click']}
                                overlay={
                                    <Menu>
                                        {totalChapters.map(c =>
                                            <Menu.Item key={c.chapter}>
                                                <a
                                                    onClick={e => {
                                                        e.preventDefault();
                                                        setChapter(c.chapter);
                                                        setRefreshKey(refreshKey + 1);
                                                    }}
                                                >
                                                    {c.chapter}
                                                </a>
                                            </Menu.Item>
                                        )}
                                    </Menu>
                                }
                            >
                                <span style={{ cursor: "pointer", fontSize: 24, fontWeight: 600 }}>
                                    Chapter {chapter} <DownOutlined />
                                </span>
                            </Dropdown>
                        </div>

                        <div style={{ marginBottom: 16, color: '#888', fontSize: 13 }}>
                            Click a word for details. Right-click to play audio from that word.
                        </div>

                        {/* Verses Display */}
                        <div className={`${lang} ${lang === 'greek' ? 'greek-content' : 'hebrew-content'}`}>
                            {verseNumbers.map(verseNum => (
                                <span key={verseNum} className="verse-container">
                                    <sup className="verse-number">{verseNum}</sup>
                                    {verseGroups[verseNum].map((word: any, idx: number) => {
                                        const prevWord = idx > 0 ? verseGroups[verseNum][idx - 1].word : '';
                                        const prevEndsMaqaf = prevWord.endsWith('־');
                                        const thisEndsMaqaf = word.word.endsWith('־');
                                        // Find word index in allVerses for playback
                                        const wordIndex = allVerses.findIndex(v => v.id === word.id);
                                        return (
                                        <React.Fragment key={word.id}>
                                            {idx > 0 && !prevEndsMaqaf && ' '}
                                            <WordLink
                                                verseData={word}
                                                lang={lang}
                                                onWordClick={handleWordClick}
                                                onPlayFromHere={() => startPlaybackFromIndex(wordIndex)}
                                                isHighlighted={word.id === currentWordId}
                                                style={thisEndsMaqaf ? { marginInlineEnd: '-0.2em' } : undefined}
                                            />
                                        </React.Fragment>
                                    );})}
                                    {' '}
                                </span>
                            ))}
                        </div>

                        {/* Definition Modal */}
                        <Modal
                            title="Definition"
                            open={isModalVisible}
                            onCancel={closeModal}
                            footer={
                                <Button onClick={closeModal}>Close</Button>
                            }
                            width={800}
                        >
                            {Object.keys(definition).length > 0 && (
                                <Table
                                    dataSource={[{
                                        ...definition,
                                        // Use the actual word from the verse, not the glossary's stored word
                                        word: selectedOccurrence?.word,
                                        definition_in_context: selectedOccurrence?.definition_in_context
                                    }]}
                                    columns={filterColumns({
                                        ...definition,
                                        word: selectedOccurrence?.word,
                                        definition_in_context: selectedOccurrence?.definition_in_context
                                    }, lang, selectedOccurrence?.word)}
                                    pagination={false}
                                    rowKey="word"
                                    size="small"
                                />
                            )}
                        </Modal>
                    </Content>
                )}
                <Footer style={{ textAlign: 'center' }}>NotByBreadAlone.io | <a href="mailto:notbybreadalone.io@gmail.com">Contact</a></Footer>
            </Layout>
        </div>
    );
}

export default Reader;
